// CursorEditorPanel — the 24×24 pixel-art editor for a self-drawn custom
// cursor (custom-cursor-2026-09-19.md §2.5 "エディタ"). Pure UI + local
// editing state; all wire/persistence side-effects (`member.cursor-change` /
// `member.cursor-draft` / localStorage) are the caller's job (`SessionProvider`
// via `useSession().sendCursorChange` / `sendCursorDraft`) — this component
// only calls the callback props.
//
// **色は既存の色 UI をそのまま使う** (作者判断、§2.5): this editor does NOT
// have its own color picker. It paints with the app's current color
// (`currentColor` prop, from the shared chrome's `pen.color`/`pen.alpha`) —
// change the color via the existing palette/wheel/ARGB editor, then paint
// here. The one exception is the eyedropper: when the app's spuit tool is
// selected (`spuitMode`), clicking a cell in *this* grid picks that cell's
// color into the current color (`onSpuitPick`) instead of painting —
// clicking the CANVAS still uses the existing (unrelated) spuit behaviour,
// this component never touches the canvas.
//
// Both layouts (Classic `CursorEditorPopup` / Modern's panel wrapper) share
// this one component; only the floating chrome around it differs.

import type { CustomCursor } from '@oekaki/protocol';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isPenEraserTipPointer } from '../classic/penEraserTip.js';
import {
  shouldEnterSpuitDragMode,
  shouldPickOnPointerDown,
  shouldSkipStrokeOnPointerDown,
} from '../classic/spuit.js';
import { CustomCursorCanvas } from './CustomCursorCanvas.js';
import {
  CURSOR_EDITOR_CELL_PX_LARGE,
  CURSOR_EDITOR_CELL_PX_MEDIUM,
  CURSOR_EDITOR_CELL_PX_SMALL,
} from './cursorEditorCellPx.js';
import { drawCustomCursor } from './customCursorDraw.js';
import {
  CURSOR_SIZE,
  type CursorRgba,
  cellFromPoint,
  clearAllCursorPixels,
  createEmptyCursorPixels,
  decodeCursorPixels,
  encodeCursorPixels,
  getCursorPixel,
  setCursorPixel,
} from './customCursorPixels.js';
import { SettingCheckboxRow } from './SettingCheckboxRow.js';

/**
 * Editor grid cell size, canvas-buffer px (24 cells × 10px = 240×240 box).
 * This is also the "小" (small) preset and the fallback when a caller hasn't
 * wired up `cellPx` (kept exported/unchanged so existing default-size
 * behaviour is untouched).
 */
export const EDITOR_CELL_PX = CURSOR_EDITOR_CELL_PX_SMALL;
/** Draft broadcast throttle — mirrors the server-side cap (§2.4 "最大 5 回/秒"). */
export const DRAFT_THROTTLE_MS = 200;

const rgb24ToRgb = (rgb24: number): { r: number; g: number; b: number } => ({
  r: (rgb24 >> 16) & 0xff,
  g: (rgb24 >> 8) & 0xff,
  b: rgb24 & 0xff,
});

const rgbToRgb24 = (r: number, g: number, b: number): number => (r << 16) | (g << 8) | b;

export interface CursorEditorPanelProps {
  /** Existing cursor to load for editing; `null` starts from a blank (fully transparent) grid. */
  readonly initialCursor: CustomCursor | null;
  /** App's current draw color (rgb24 0..0xFFFFFF) + alpha (0..255) — "現在色". */
  readonly currentColor: { readonly rgb24: number; readonly alpha: number };
  /** Whether the app's spuit (eyedropper) tool is currently selected (shared chrome `spuitMode`). */
  readonly spuitMode: boolean;
  /** Adopt a picked color into the app's current color (spuit-on-grid, §2.5 "スポイト"). */
  readonly onSpuitPick: (color: { rgb24: number; alpha: number }) => void;
  /**
   * Live in-progress state while the editor is open, throttled to
   * `DRAFT_THROTTLE_MS` and only sent when the pixels actually changed
   * (§2.4). `cursor: null` on close (confirm/cancel/remove/unmount).
   */
  readonly onDraft: (cursor: CustomCursor | null) => void;
  /** 「決定」— confirm the current grid as the new cursor. */
  readonly onConfirm: (cursor: CustomCursor) => void;
  /** 「やめる」— close without changing anything. */
  readonly onCancel: () => void;
  /** 「カーソルを外す」— clear the cursor entirely (not just this edit session). */
  readonly onRemove: () => void;
  /**
   * 1 マスの表示サイズ (canvas-buffer px、既定 `EDITOR_CELL_PX` = 10)。作者
   * staging フィードバック 2026-09-19 follow-up 「パネルをリサイズして大きく
   * したい」— 呼び出し側 (Classic `CursorEditorPopup` / Modern
   * `ModernCursorEditorPanel`) が `useCursorEditorCellPxResize` で持つ controlled
   * 値。プリセットボタン (小/中/大、本 component 内) とパネル右下の角ドラッグ
   * (呼び出し側) の両方がこの同じ値を更新するので、常に同期している。
   */
  readonly cellPx?: number;
  /** `cellPx` 変更時 (プリセットボタン押下)。省略時はプリセットボタンを出さない。 */
  readonly onCellPxChange?: (next: number) => void;
  /**
   * 自分の確定済みカスタムカーソルを自分の画面にも表示するか (controlled、作者
   * staging フィードバック 2026-09-19 follow-up)。`PenSettingsPanel` の同じ設定と
   * 共有 (chrome 側の単一 state)。省略時はチェックボックス自体を出さない
   * (= 既存テスト等、この設定に触れない呼び出し側への後方互換)。
   */
  readonly showOwnCustomCursor?: boolean;
  readonly onShowOwnCustomCursorChange?: (next: boolean) => void;
}

type EditorTool = 'pen' | 'eraser';

export const CursorEditorPanel = ({
  initialCursor,
  currentColor,
  spuitMode,
  onSpuitPick,
  onDraft,
  onConfirm,
  onCancel,
  onRemove,
  cellPx = EDITOR_CELL_PX,
  onCellPxChange,
  showOwnCustomCursor,
  onShowOwnCustomCursorChange,
}: CursorEditorPanelProps): React.ReactNode => {
  const [pixels, setPixels] = useState<Uint8ClampedArray>(() =>
    initialCursor !== null ? decodeCursorPixels(initialCursor) : createEmptyCursorPixels(),
  );
  const [tool, setTool] = useState<EditorTool>('pen');
  const [previewScale, setPreviewScale] = useState<1 | 2>(1);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isPaintingRef = useRef<boolean>(false);
  // 右クリック / Alt+クリック / ペンのバレルボタンによる単発スポイト (DrawingCanvas
  // と同じ判定、`../classic/spuit.js` を直接 import して二重実装しない)。マウス
  // 右クリックは pointerdown → contextmenu の順で両方発火するため、pointerdown
  // 側で既にピック済みのポインタを覚えておいて contextmenu 側の二重ピックを防ぐ
  // (`DrawingCanvas.tsx` の `spuitPickedOnPointerDownRef` と同じガード)。
  const spuitPickedOnPointerDownRef = useRef<number | null>(null);
  // 右クリック / Alt+クリックを押したままのドラッグで連続スポイト
  // (`shouldEnterSpuitDragMode`、Photoshop 等の eyedropper 踏襲、DrawingCanvas と同じ).
  const spuitDragRef = useRef<number | null>(null);

  // ─── draft throttle (§2.4: caller must throttle to ≤5/s, only on change) ──
  const lastSentAtRef = useRef<number>(0);
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Snapshot to send on the trailing edge — always the MOST RECENT pixels
  // passed to `scheduleDraft`, not whatever `pixels` state happens to be
  // when the timer fires (there is no re-render between them either way,
  // but keeping an explicit snapshot avoids relying on a stale closure).
  const pendingSnapshotRef = useRef<Uint8ClampedArray | null>(null);
  const onDraftRef = useRef(onDraft);
  onDraftRef.current = onDraft;

  const scheduleDraft = useCallback((snapshot: Uint8ClampedArray) => {
    const now = Date.now();
    const elapsed = now - lastSentAtRef.current;
    const send = (pixelsToSend: Uint8ClampedArray): void => {
      lastSentAtRef.current = Date.now();
      onDraftRef.current(encodeCursorPixels(pixelsToSend));
    };
    if (elapsed >= DRAFT_THROTTLE_MS) {
      if (pendingTimerRef.current !== null) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
      send(snapshot);
      return;
    }
    pendingSnapshotRef.current = snapshot;
    if (pendingTimerRef.current !== null) return; // a trailing send is already scheduled
    pendingTimerRef.current = setTimeout(() => {
      pendingTimerRef.current = null;
      const toSend = pendingSnapshotRef.current;
      pendingSnapshotRef.current = null;
      if (toSend !== null) send(toSend);
    }, DRAFT_THROTTLE_MS - elapsed);
  }, []);

  // Announce "editing ended" immediately (bypassing the throttle) and drop
  // any pending trailing send whenever the editor closes for any reason.
  const endDraft = useCallback(() => {
    if (pendingTimerRef.current !== null) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
    onDraftRef.current(null);
  }, []);
  useEffect(() => endDraft, [endDraft]);

  const paintCell = useCallback(
    (x: number, y: number, spuitPickInstead: boolean, forceErase = false): void => {
      if (spuitPickInstead) {
        const px = getCursorPixel(pixels, x, y);
        if (px.a === 0) return; // §2.5: 透明なら何もしない
        onSpuitPick({ rgb24: rgbToRgb24(px.r, px.g, px.b), alpha: px.a });
        return;
      }
      // ペン先反転 (eraser tip) は消しゴムツール選択中と同じ扱いにする
      // (DrawingCanvas の `isPenEraserTipPointer` override と同じ発想、キャンバスの
      // 消しゴムに対応). `tool` (UI 上の選択) 自体は変えない.
      const useEraser = tool === 'eraser' || forceErase;
      const color: CursorRgba = useEraser
        ? { r: 0, g: 0, b: 0, a: 0 }
        : { ...rgb24ToRgb(currentColor.rgb24), a: currentColor.alpha };
      setPixels((prev) => {
        const next = setCursorPixel(prev, x, y, color);
        if (next !== prev) scheduleDraft(next);
        return next;
      });
    },
    [pixels, tool, currentColor, onSpuitPick, scheduleDraft],
  );

  const cellFromEvent = (
    e: { readonly clientX: number; readonly clientY: number },
    target: HTMLElement,
  ): { x: number; y: number } | null => {
    const rect = target.getBoundingClientRect();
    return cellFromPoint(e.clientX - rect.left, e.clientY - rect.top, cellPx);
  };

  // 右クリック/Alt/ペンバレルの単発スポイト・連続ドラッグ・ペン先消しゴムは
  // `DrawingCanvas.tsx` (キャンバス本体) と同じ操作感にする (作者 staging
  // フィードバック 2026-09-19 follow-up「基本的にキャンバスと同じ操作感にしたい」)。
  // 判定そのものは `../classic/spuit.js` / `../classic/penEraserTip.js` の純関数を
  // そのまま import して再利用する (= 二重実装しない、DrawingCanvas と条件が
  // ズレる心配がない). この editor は同期の in-memory pixel 配列 (worker 越しの
  // 非同期 sampling が無い) なので、DrawingCanvas 側の `SpuitPickCoalescer`
  // (worker 往復の順不同 resolve 対策) は不要 — 都度 `paintCell` を直接呼ぶだけで
  // 十分.
  const onGridPointerDown = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    const cell = cellFromEvent(e, e.currentTarget);
    if (cell === null) return;

    if (shouldSkipStrokeOnPointerDown(e)) {
      if (shouldPickOnPointerDown(e)) {
        paintCell(cell.x, cell.y, true);
        // 直後に来る `contextmenu` (マウス右クリックは必ず発火) で同じ位置を二重に
        // 拾わないようにする (DrawingCanvas と同じガード).
        spuitPickedOnPointerDownRef.current = e.pointerId;
      }
      if (shouldEnterSpuitDragMode(e)) {
        spuitDragRef.current = e.pointerId;
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          // defensive — jsdom 等 pointer capture 未実装環境.
        }
      }
      return;
    }

    // 中クリック等の誤操作 (パネルが意図せず動く/選択される事故を防ぐ)。ペン先
    // 消しゴムは `button === 5` で報告される (PointerEvent 仕様) が正規の描画
    // 入力なので除外しない.
    if (e.button !== 0 && !isPenEraserTipPointer(e)) return;

    if (spuitMode) {
      paintCell(cell.x, cell.y, true);
      return; // §2.5: spuit tool is single-click, not a drag-paint gesture
    }
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // defensive — some test/embedding environments (jsdom) don't implement
      // pointer capture; painting still works via the plain pointermove path.
    }
    isPaintingRef.current = true;
    paintCell(cell.x, cell.y, false, isPenEraserTipPointer(e));
  };
  const onGridPointerMove = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    const cell = cellFromEvent(e, e.currentTarget);
    if (cell === null) return;
    if (spuitDragRef.current === e.pointerId) {
      paintCell(cell.x, cell.y, true);
      return;
    }
    if (!isPaintingRef.current) return;
    paintCell(cell.x, cell.y, false, isPenEraserTipPointer(e));
  };
  const endPaint = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    if (spuitDragRef.current === e.pointerId) {
      spuitDragRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // defensive
      }
    }
    if (!isPaintingRef.current) return;
    isPaintingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // defensive — capture may already be gone (e.g. pointercancel).
    }
  };
  // 右クリックのブラウザ既定メニューを常に抑止する (DrawingCanvas と同じ). 初回
  // ピックは pointerdown 側で済ませているのが通常経路 (マウスは pointerdown →
  // contextmenu の順で両方発火する) なので、既にガードが立っていれば二重ピックを
  // skip するだけ. pointerdown 側で拾えなかった環境向けのフォールバックとして、
  // ガードが無ければここで pick する.
  const onGridContextMenu = (e: React.MouseEvent<HTMLCanvasElement>): void => {
    e.preventDefault();
    if (spuitPickedOnPointerDownRef.current !== null) {
      spuitPickedOnPointerDownRef.current = null;
      return;
    }
    const cell = cellFromEvent(e, e.currentTarget);
    if (cell === null) return;
    paintCell(cell.x, cell.y, true);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return; // jsdom (tests) has no Canvas2D backend.
    drawCustomCursor(ctx, pixels, { cellPx, checker: true, grid: true });
  }, [pixels, cellPx]);

  const handleClearAll = (): void => {
    setPixels(() => {
      const next = clearAllCursorPixels();
      scheduleDraft(next);
      return next;
    });
  };

  const handleConfirm = (): void => {
    endDraft();
    onConfirm(encodeCursorPixels(pixels));
  };
  const handleCancel = (): void => {
    endDraft();
    onCancel();
  };
  const handleRemove = (): void => {
    endDraft();
    onRemove();
  };

  const gridSizePx = cellPx * CURSOR_SIZE;
  const currentColorCss = useMemo(() => {
    const { r, g, b } = rgb24ToRgb(currentColor.rgb24);
    return `rgba(${r}, ${g}, ${b}, ${currentColor.alpha / 255})`;
  }, [currentColor]);

  return (
    <div data-testid="cursor-editor-panel" className="flex flex-col gap-2 text-white">
      <div className="flex items-center gap-2 text-[11px]">
        <span
          aria-hidden="true"
          data-testid="cursor-editor-current-color-swatch"
          className="h-4 w-4 shrink-0 border border-black"
          style={{ backgroundColor: currentColorCss }}
        />
        <span>現在色で描画{spuitMode ? '（スポイト中: マスをクリックで色を取得）' : ''}</span>
      </div>

      <canvas
        ref={canvasRef}
        data-testid="cursor-editor-grid"
        width={gridSizePx}
        height={gridSizePx}
        style={{
          width: gridSizePx,
          height: gridSizePx,
          touchAction: 'none',
          imageRendering: 'pixelated',
        }}
        className="cursor-crosshair self-start border border-black"
        onPointerDown={onGridPointerDown}
        onPointerMove={onGridPointerMove}
        onPointerUp={endPaint}
        onPointerCancel={endPaint}
        onContextMenu={onGridContextMenu}
      />

      {/* 作者 staging フィードバック 2026-09-19 follow-up: 「パネルをリサイズして
          大きくしたい。画面の解像度によっては小さくてドット絵が描きづらい」。
          プリセットボタン (小/中/大) はもう 1 つの入口 (パネル右下の角ドラッグ) と
          同じ `cellPx`/`onCellPxChange` を共有するので、どちらで変えても両方の
          表示に反映される。`onCellPxChange` 未指定の呼び出し側 (= 既存テスト等)
          ではボタン自体を出さない. */}
      {onCellPxChange !== undefined && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-[10px] text-[var(--color-text-muted)]">マスの大きさ</span>
          {(
            [
              { value: CURSOR_EDITOR_CELL_PX_SMALL, label: '小' },
              { value: CURSOR_EDITOR_CELL_PX_MEDIUM, label: '中' },
              { value: CURSOR_EDITOR_CELL_PX_LARGE, label: '大' },
            ] as const
          ).map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => onCellPxChange(preset.value)}
              aria-pressed={cellPx === preset.value}
              className={`border border-black px-2 py-0.5 text-[10px] font-bold ${
                cellPx === preset.value
                  ? 'bg-[var(--color-accent)] text-black'
                  : 'bg-[var(--color-bg-soft)]'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1">
        <button
          type="button"
          onClick={() => setTool('pen')}
          aria-pressed={tool === 'pen'}
          className={`border border-black px-2 py-0.5 text-[10px] font-bold ${
            tool === 'pen' ? 'bg-[var(--color-accent)] text-black' : 'bg-[var(--color-bg-soft)]'
          }`}
        >
          ペン
        </button>
        <button
          type="button"
          onClick={() => setTool('eraser')}
          aria-pressed={tool === 'eraser'}
          className={`border border-black px-2 py-0.5 text-[10px] font-bold ${
            tool === 'eraser' ? 'bg-[var(--color-accent)] text-black' : 'bg-[var(--color-bg-soft)]'
          }`}
        >
          消しゴム
        </button>
        <button
          type="button"
          onClick={handleClearAll}
          className="border border-black bg-[var(--color-bg-soft)] px-2 py-0.5 text-[10px] font-bold"
        >
          全消し
        </button>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[10px] text-[var(--color-text-muted)]">プレビュー</span>
        <button
          type="button"
          onClick={() => setPreviewScale((s) => (s === 1 ? 2 : 1))}
          aria-pressed={previewScale === 2}
          className="border border-black bg-[var(--color-bg-soft)] px-2 py-0.5 text-[10px] font-bold"
        >
          {previewScale === 1 ? '1x' : '2x'}
        </button>
        <CustomCursorCanvas
          pixels={pixels}
          sizePx={24 * previewScale}
          checker
          alwaysRender
          title="カーソルのプレビュー"
        />
      </div>

      {/* 作者 staging 確認フィードバック 2026-09-19 follow-up4: スイッチが文言と
          離れて分かりづらい → チェックボックスに戻し、ラベル文字を強調 (`PenSettingsPanel`
          「ブラシカーソル」直下の同じ行と見た目を揃える、共有する controlled 値 =
          chrome 側の単一 state)。プレビューの直下に置く (= 位置は従来どおり)。 */}
      {onShowOwnCustomCursorChange !== undefined && (
        <SettingCheckboxRow
          label="自分の画面にも表示"
          description="OFF にすると自分の画面からだけ消えます。相手には見えたままです"
          checked={showOwnCustomCursor ?? true}
          onChange={onShowOwnCustomCursorChange}
        />
      )}

      <div className="flex flex-wrap items-center gap-1 border-t border-black pt-2">
        <button
          type="button"
          onClick={handleConfirm}
          className="border border-black bg-[var(--color-accent)] px-2 py-1 text-[11px] font-bold text-black"
        >
          決定
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className="border border-black bg-[var(--color-bg-soft)] px-2 py-1 text-[11px] font-bold"
        >
          やめる
        </button>
        <button
          type="button"
          onClick={handleRemove}
          className="ml-auto border border-black bg-[var(--color-error)] px-2 py-1 text-[11px] font-bold text-white"
        >
          カーソルを外す
        </button>
      </div>
    </div>
  );
};
